import React, { useMemo, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Separator } from '../../ui/separator';
import type { SshProfile } from '../../../types/ssh';
import { SshProfileForm } from './SshProfileForm';

interface SshProfileManagerProps {
  profiles: SshProfile[];
  onSaveProfile: (profile: SshProfile) => void;
  onDeleteProfile: (profileId: string) => void;
  isElectron: boolean;
}

export function SshProfileManager({
  profiles,
  onSaveProfile,
  onDeleteProfile,
  isElectron,
}: SshProfileManagerProps) {
  const [editingProfileId, setEditingProfileId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const editingProfile = useMemo(
    () => profiles.find((profile) => profile.id === editingProfileId) || null,
    [editingProfileId, profiles],
  );

  return (
    <div className="space-y-4">
      <Card className="border-border/60 bg-black/20 shadow-none">
        <CardHeader className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle className="text-sm font-semibold text-white">SSH Profiles</CardTitle>
              <CardDescription className="text-xs text-gray-400">
                Reuse saved MapleOS terminal SSH targets with inline password prompts.
              </CardDescription>
            </div>
            <Button
              onClick={() => {
                setEditingProfileId(null);
                setIsCreating(true);
              }}
              size="sm"
              className="bg-emerald-600 text-white hover:bg-emerald-500"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add profile
            </Button>
          </div>
          {!isElectron && (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
              SSH connections only work in the MapleOS desktop app. Profiles can still be prepared here.
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {profiles.length === 0 ? (
            <div className="rounded-md border border-dashed border-border/60 bg-black/20 px-4 py-6 text-sm text-gray-400">
              No SSH profiles saved yet.
            </div>
          ) : (
            profiles.map((profile, index) => (
              <React.Fragment key={profile.id}>
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div className="space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-white">{profile.name}</span>
                      <Badge variant="secondary" className="bg-white/10 text-gray-200">
                        {profile.username}@{profile.host}:{profile.port}
                      </Badge>
                      <Badge variant="outline" className="border-border/60 text-gray-300">
                        {profile.authType === 'privateKey' ? 'Private key' : 'Password'}
                      </Badge>
                    </div>
                    <div className="text-xs text-gray-400">
                      Connect with <span className="font-mono text-gray-200">ssh {profile.name}</span>
                    </div>
                    {profile.authType === 'privateKey' && profile.privateKeyName && (
                      <div className="text-xs text-gray-500">Key label: {profile.privateKeyName}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditingProfileId(profile.id);
                        setIsCreating(false);
                      }}
                      className="border-border/60 bg-transparent text-white hover:bg-white/10"
                    >
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onDeleteProfile(profile.id)}
                      className="border-red-500/40 bg-transparent text-red-200 hover:bg-red-500/10"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </Button>
                  </div>
                </div>
                {index < profiles.length - 1 && <Separator className="bg-border/50" />}
              </React.Fragment>
            ))
          )}
        </CardContent>
      </Card>

      {(isCreating || editingProfile) && (
        <SshProfileForm
          profile={editingProfile}
          onCancel={() => {
            setEditingProfileId(null);
            setIsCreating(false);
          }}
          onSave={(profile) => {
            onSaveProfile(profile);
            setEditingProfileId(null);
            setIsCreating(false);
          }}
        />
      )}
    </div>
  );
}
